/**
 * Cloud Storage. The third file of the Firebase trio, and the same deal as
 * `firebase-auth.js`: ARCHITECTURE.md §3 says the SDK is touched in ONE
 * place, and `firebase.js` re-exports everything below, so no feature imports
 * this file and no importer changed.
 *
 * The Storage INSTANCE is not created here. `firebase.js` owns the lifecycle
 * and hands the app over with `bindStorage()` during `initFirebase()`, so the
 * dependency points one way (firebase.js -> firebase-storage.js) and there is
 * no cycle to break the first time somebody reorders a statement.
 *
 * ── Storage needs Blaze ───────────────────────────────────────────────────
 * Cloud Storage is unavailable on the Spark plan. The project is on Blaze
 * (HANDOFF §4, checked 14 Aug 2026 — "Blaze | Free Trial") so this works, but
 * every call below fails softly rather than throwing an unhandled rejection,
 * because a billing change is a thing that happens to a project without the
 * code hearing about it.
 */

import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'

/** @type {any} */
let storage = null

/**
 * Hand this module the Firebase app. Called once, by `initFirebase()`.
 * Lazily creates the Storage instance so a session that never uploads
 * anything never pays for the client.
 * @param {any} app
 */
export function bindStorage(app) {
  storage = app ? getStorage(app) : null
}

/** @returns {boolean} whether uploads are possible at all */
export function isStorageReady() {
  return Boolean(storage)
}

function requireStorage() {
  if (!storage) throw new Error('Storage is not ready. Await initFirebase() first.')
  return storage
}

/**
 * Upload bytes and return the URL to read them back.
 *
 * `getDownloadURL` is called on the SAME reference the upload returned, not
 * on a path rebuilt from strings — a rebuilt path is how you end up serving
 * the previous advisor's logo after a rename.
 *
 * The returned URL carries a download token. That token is a CAPABILITY:
 * anybody holding the URL can fetch the object regardless of what
 * `storage.rules` says about reads. That is why the rules file puts the logo
 * under `.../public/` and says so out loud, instead of pretending a token is
 * an access control. See `storage.rules`.
 *
 * @param {string} path - e.g. `users/<uid>/public/logo-1723800000000.png`
 * @param {Blob} blob
 * @param {{contentType?: string, cacheControl?: string}} [meta]
 * @returns {Promise<string>} the download URL
 */
export async function uploadBytesTo(path, blob, meta = {}) {
  const target = ref(requireStorage(), path)
  await uploadBytes(target, blob, {
    contentType: meta.contentType || blob.type || 'application/octet-stream',
    /* Objects are written under a timestamped name and never overwritten, so
       a long immutable cache is safe and means the public intake page fetches
       an advisor's logo once rather than on every visitor's page load. */
    cacheControl: meta.cacheControl || 'public, max-age=31536000, immutable',
  })
  return getDownloadURL(target)
}

/**
 * Delete one object. Returns false rather than throwing when the object is
 * already gone — this is called to clean up a SUPERSEDED logo, and failing
 * that clean-up must never fail the upload that replaced it.
 *
 * @param {string} path
 * @returns {Promise<boolean>}
 */
export async function deleteStoredObject(path) {
  try {
    await deleteObject(ref(requireStorage(), path))
    return true
  } catch (err) {
    console.warn('storage delete failed (ignored)', path, err)
    return false
  }
}
